import React, { useState, useEffect } from 'react';
import { getUserProfile, updateUserProfile } from '../firebase';
import { INDUSTRY_LIST, DEFAULT_INDUSTRY } from '../industryConfig';

const BRAND = '#0f6e56';

// Resize + compress an image file to a base64 data URL (keeps Firestore doc small)
function fileToResizedDataURL(file, maxDim = 320, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
        else if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function CompanySettings({ user }) {
  const [form, setForm] = useState({ businessName: '', ownerName: '', address: '', city: '', country: '', phone: '', email: '', taxNumber: '', currency: 'USD', logo: '', industry: DEFAULT_INDUSTRY });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getUserProfile(user.uid).then(p => {
      if (p) setForm(f => ({ ...f, ...p }));
      setLoading(false);
    });
  }, []);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const onLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setMsg('Please select an image file.'); return; }
    try {
      const dataUrl = await fileToResizedDataURL(file);
      set('logo', dataUrl);
      setMsg('Logo ready — click Save to store it.');
    } catch { setMsg('Could not read that image.'); }
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await updateUserProfile(user.uid, form);
      setSaved(true); setMsg('Company profile saved. It will now appear on your invoices.');
    } catch (err) { setMsg('Save failed: ' + err.message); }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Company Settings</h2>
      <div style={{ color: '#888', fontSize: 14, marginBottom: 22 }}>Your company details and logo appear on invoices and reports.</div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', padding: 24, marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>Company Logo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 96, height: 96, borderRadius: 12, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', overflow: 'hidden' }}>
            {form.logo ? <img src={form.logo} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <span style={{ color: '#bbb', fontSize: 12 }}>No logo</span>}
          </div>
          <div>
            <label style={{ ...btn, display: 'inline-block', cursor: 'pointer' }}>
              Upload Logo
              <input type="file" accept="image/*" onChange={onLogo} style={{ display: 'none' }} />
            </label>
            {form.logo && <button onClick={() => set('logo', '')} style={{ marginLeft: 10, padding: '10px 16px', borderRadius: 10, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', color: '#a32d2d' }}>Remove</button>}
            <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>PNG or JPG. Auto-resized for fast loading.</div>
          </div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', padding: 24, marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Industry / Edition</div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Choose how FinlyBooks adapts your invoices, fields and labels. You can switch anytime.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
          {INDUSTRY_LIST.map(ind => {
            const active = form.industry === ind.id;
            return (
              <button key={ind.id} type="button" onClick={() => set('industry', ind.id)}
                style={{ textAlign: 'left', padding: 16, borderRadius: 12, cursor: 'pointer',
                  border: `2px solid ${active ? ind.accent : '#e2e2e2'}`,
                  background: active ? ind.accent + '10' : '#fff' }}>
                <div style={{ fontSize: 22, marginBottom: 6 }}>{ind.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: active ? ind.accent : '#333' }}>{ind.name}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 3, lineHeight: 1.4 }}>{ind.tagline}</div>
                {active && <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: ind.accent }}>✓ SELECTED</div>}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', padding: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16 }}>Business Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <F label="Company / Agency Name" value={form.businessName} onChange={v => set('businessName', v)} placeholder="e.g. Al Rihla Travels" />
          <F label="Owner Name" value={form.ownerName} onChange={v => set('ownerName', v)} />
          <F label="Phone" value={form.phone} onChange={v => set('phone', v)} />
          <F label="Email" value={form.email} onChange={v => set('email', v)} />
          <F label="Address" value={form.address} onChange={v => set('address', v)} />
          <F label="City" value={form.city} onChange={v => set('city', v)} />
          <F label="Country" value={form.country} onChange={v => set('country', v)} />
          <F label="Tax / TRN / VAT No." value={form.taxNumber} onChange={v => set('taxNumber', v)} />
          <F label="Base Currency (your books)" value={form.currency} onChange={v => set('currency', v)} placeholder="e.g. QAR, USD, INR" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 20 }}>
          <button onClick={save} disabled={saving} style={{ ...btn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving...' : 'Save Company Profile'}</button>
          {msg && <span style={{ fontSize: 13, color: saved ? BRAND : '#854f0b' }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 6 }}>{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inp} />
    </div>
  );
}

const btn = { padding: '10px 20px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const inp = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, color: '#222', outline: 'none', background: '#fff' };
